import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button, Divider, ToastMessage } from '@/components'
import { Icons } from '@/theme/Images'
import { useLinkToken } from '../pages/hooks/use-link-token'
import { toast } from 'sonner'
import { usePlaidLink } from 'react-plaid-link'
import Spinner from '@/assets/icons/spinner'
import usePlaidItems from '../pages/hooks/use-plaid-items'
import { syncPlaidItems } from '../api/payment/plaid'
import { saveUpdateModalDismissed, getUpdateModalDismissed } from '../storage/plaid-provider'
import PropTypes from 'prop-types'
import { PLAID_ITEM_STATUS } from '@/data/constants'

const RECONNECT_ITEM_STATUS = [PLAID_ITEM_STATUS.PENDING_DISCONNECT, PLAID_ITEM_STATUS.DISCONNECTED]

// TODO: This could be simplified by using the PlaidLink component
const PlaidUpdateModal = () => {
  const { plaidItems } = usePlaidItems()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)

  // Get the first item that needs reconnection for update mode
  const itemToReconnect =
    plaidItems && Array.isArray(plaidItems)
      ? plaidItems.find(item => RECONNECT_ITEM_STATUS.includes(item.itemStatus))
      : null

  // Plaid link setup - use itemId for update mode
  const { data: linkTokenData, isFetching: isFetchingLinkToken } = useLinkToken({
    itemId: itemToReconnect?.plaidItemId,
  })

  // Show modal if conditions are met (only if not dismissed this session)
  useEffect(() => {
    // Only show if hasn't been dismissed this session
    if (!getUpdateModalDismissed()) {
      // Small delay to ensure the modal shows after the page loads
      const timer = setTimeout(async () => {
        setIsModalOpen(true)
      }, 1000)

      return () => clearTimeout(timer)
    }
  }, []) // No dependencies needed since we only check dismissal

  const handleClose = () => {
    // Show confirmation modal instead of closing directly
    setIsModalOpen(false)
    setIsConfirmationModalOpen(true)
  }

  const onSuccess = async (public_token, metadata) => {
    console.log('Plaid update completed successfully', metadata)

    try {
      // Refresh the item status to ACTIVE by syncing all Plaid items
      await syncPlaidItems()
      console.log('Successfully re-synced Plaid items after update link')
    } catch (error) {
      // DB status refresh failed
      console.error('Error refreshing Plaid item status:', error)
    }

    // Always show success toast and close modal, regardless of sync status
    setIsModalOpen(false)
    toast.custom(() => (
      <ToastMessage
        success
        text="Account successfully reconnected"
        description="You've successfully reconnected your payment account. You will continue to earn rewards on your vehicle lease and loan payments"
      />
    ))
  }

  const onExit = function (err) {
    console.error('Plaid update failed', err)
  }

  const config = {
    token: linkTokenData?.link_token,
    onSuccess,
    onExit,
  }

  const { open, ready } = usePlaidLink(config)

  const handleReconnect = () => {
    setIsModalOpen(false)
    open()
  }

  const handleConfirmationCancel = () => {
    // Return to the reconnect modal
    setIsConfirmationModalOpen(false)
    setIsModalOpen(true)
  }

  const handleConfirmationContinue = () => {
    // Dismiss both modals and set the dismissed flag - don't show again until next login
    setIsConfirmationModalOpen(false)
    setIsModalOpen(false)
    saveUpdateModalDismissed(true)
  }

  return (
    <>
      <Dialog onOpenChange={handleClose} open={isModalOpen}>
        <DialogContent
          className="max-w-[92%] rounded-lg md:max-w-[480px] overflow-y-auto max-h-[95%]"
          data-testid="plaid-update-modal-content"
        >
          <div className="flex flex-row justify-start items-start p-5 pb-2">
            <img src={Icons.chainIcon} alt="icon" width={48} />
            <DialogHeader className="pt-0 pl-4 w-[215px] md:w-[368px]">
              <div className="flex flex-col justify-center items-start">
                <DialogTitle className="leading-7 text-neutral-900 text-lg font-inter text-left">
                  Reconnect Your Account to Keep Earning Points
                </DialogTitle>
                <DialogDescription className="text-neutral-700 text-start pt-1 w-[215px] md:w-[368px]">
                  <div className="flex flex-col justify-center items-start gap-4">
                    <div>
                      <p className="font-inter text-sm font-normal leading-5 text-left">
                        It looks like your Plaid connection needs an update! To continue earning rewards on your vehicle
                        lease or loan payments and making repayments to your Fasten Rewards™ Visa Card, please
                        reconnect your payment account now. Without reconnecting, you won&apos;t be able to earn rewards
                        or make payments through our platform.
                      </p>
                    </div>
                  </div>
                </DialogDescription>
              </div>
            </DialogHeader>
          </div>
          <Divider />
          <DialogFooter className="w-full p-5">
            <Button
              id="reconnect-account-button"
              aria-label="Reconnect Account"
              type="button"
              className="w-full"
              onClick={handleReconnect}
              disabled={isFetchingLinkToken || !ready}
            >
              {isFetchingLinkToken || !ready ? <Spinner size="20" stroke="#98a2b3" /> : null}
              Reconnect Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PlaidUpdateConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={handleConfirmationContinue}
        onCancel={handleConfirmationCancel}
        onContinue={handleConfirmationContinue}
      />
    </>
  )
}

const PlaidUpdateConfirmationModal = ({ isOpen, onClose, onCancel, onContinue }) => {
  return (
    <Dialog onOpenChange={onClose} open={isOpen}>
      <DialogContent
        className="max-w-[92%] rounded-lg md:max-w-[480px] overflow-y-auto max-h-[95%]"
        data-testid="plaid-update-confirmation-modal-content"
      >
        <div className="flex flex-row justify-start items-start p-5 pb-2">
          <img src={Icons.chainIcon} alt="icon" width={48} />
          <DialogHeader className="pt-0 pl-4 w-[215px] md:w-[368px]">
            <div className="flex flex-col justify-center items-start">
              <DialogTitle className="leading-7 text-neutral-900 text-lg font-inter text-left">
                Are You Sure?
              </DialogTitle>
              <DialogDescription className="text-neutral-700 text-start pt-1 w-[215px] md:w-[368px]">
                <div className="flex flex-col justify-center items-start gap-4">
                  <div>
                    <p className="font-inter text-sm font-normal leading-5 text-left">
                      Are you sure you don&apos;t want to reconnect your account? Without reconnecting, you won&apos;t
                      be able to earn rewards on your vehicle lease or loan payments and won&apos;t be able to make
                      repayments to your Fasten Rewards™ Visa Card through our platform.
                    </p>
                  </div>
                </div>
              </DialogDescription>
            </div>
          </DialogHeader>
        </div>
        <Divider />
        <DialogFooter className="w-full p-5 gap-3">
          <Button
            id="plaid-update-cancel-button"
            aria-label="Cancel"
            variant="outlinePrimary"
            className="w-full"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            id="plaid-update-continue-button"
            aria-label="Continue"
            type="button"
            className="w-full"
            onClick={onContinue}
          >
            Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

PlaidUpdateConfirmationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
}

export default PlaidUpdateModal
